import { Link } from "react-router-dom";
import { ArrowUpRight, Bell, BriefcaseBusiness, ChevronRight, CircleCheck, Compass, FileText, Gavel, GraduationCap, MessageCircle, Scale, Sparkles, Users } from "lucide-react";
import { address, certificates, courses, directionsUrl, googleReviews, institutes, mapEmbed, skills, testimonials, whatsapp } from "@/data/site";
import { articles, featuredArticles } from "@/data/journal";
import { programmes } from "@/data/programmes";
import ArticleCard from "@/components/ArticleCard";

export default function HomePage() {
  const go = id => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <main>
      <section id="home" className="hero section-pad">
        <div className="hero-copy">
          <div className="eyebrow"><span />Practical legal education</div>
          <h1>Law college teaches you the law.<em> We teach you how to use it.</em></h1>
          <p className="hero-lead">Turn classroom knowledge into practical confidence for internships, chambers, courtrooms and the career ahead.</p>
          <div className="hero-buttons">
            <button className="primary-btn" data-testid="hero-explore-courses-button" onClick={() => go("courses")}>Explore courses <ArrowUpRight size={18} /></button>
            <a className="outline-btn" data-testid="hero-call-chat-button" href={whatsapp} target="_blank" rel="noreferrer"><MessageCircle size={18} /> Talk to ADVOKIT</a>
          </div>
          <div className="hero-proof">
            <div className="avatars">{testimonials.slice(0, 3).map(t => <span key={t.name}>{t.name.split(" ").map(n => n[0]).join("")}</span>)}</div>
            <p><strong data-testid="student-trust-stat">2,500+</strong> learners building their edge<br /><small>with mentors from the real world of law</small></p>
          </div>
        </div>
        <div className="hero-visual">
          <div className="image-frame">
            <img src="https://images.pexels.com/photos/8693379/pexels-photo-8693379.jpeg" alt="Legal professional working at a desk" />
            <div className="image-stamp"><Scale size={18} /><span>From classroom<br /><b>to courtroom</b></span></div>
          </div>
          <div className="hero-note"><span>01</span><p>Practical skills.<br /><b>Professional confidence.</b></p></div>
        </div>
      </section>

      <section className="maroon-strip">
        <div><Sparkles size={17} />Learn by doing</div>
        <div><CircleCheck size={17} />Guided by practitioners</div>
        <div><BriefcaseBusiness size={17} />Built for careers</div>
        <div><Scale size={17} />Grounded in practice</div>
      </section>

      <section className="institutes-band" data-testid="institutes-marquee">
        <div className="institutes-head">Institutes that trust our training</div>
        <div className="marquee">
          <div className="marquee-track">
            {[...institutes, ...institutes].map((inst, i) => inst.logo
              ? <img key={i} src={inst.logo} alt={`${inst.name} logo`} data-testid={`institute-logo-${i + 1}`} loading="lazy" />
              : <span key={i} className="marquee-name" data-testid={`institute-name-${i + 1}`}>{inst.name}</span>)}
          </div>
        </div>
      </section>

      <section id="skills" className="section-pad skills-section">
        <div className="section-intro">
          <div><div className="eyebrow">The ADVOKIT difference</div><h2>Close the gap between <em>knowing</em> and doing.</h2></div>
          <p>Real legal work is more than remembering a section. It is knowing what to do next, how to think clearly and how to communicate with confidence.</p>
        </div>
        <div className="skills-grid">
          {skills.map(([Icon, title, desc], i) => (
            <article className="skill-card" key={title} data-testid={`skill-card-${i + 1}`}>
              <div className="skill-icon"><Icon size={21} /></div>
              <span className="card-number">0{i + 1}</span>
              <h3>{title}</h3>
              <p>{desc}</p>
              <button data-testid={`skill-learn-more-${i + 1}`} onClick={() => go("courses")}>Explore skill <ChevronRight size={16} /></button>
            </article>
          ))}
        </div>
      </section>

      <section id="courses" className="section-pad courses-section">
        <div className="section-heading-row">
          <div><div className="eyebrow gold">Current programmes</div><h2>Choose your next <em>professional move.</em></h2></div>
          <p>Focused programmes. Real assignments. Feedback that changes how you work.</p>
        </div>
        <div className="course-grid">
          {courses.map(c => (
            <article className="course-card" key={c.id} data-testid={c.id}>
              <Link className="course-image" data-testid={`${c.id}-banner-link`} to={c.path}><img src={c.image} alt="" /><span className="course-number">{c.num}</span><span className="course-badge">Live + practical</span></Link>
              <div className="course-content">
                <span className="eyebrow">{c.type}</span>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
                <div className="tag-row">{c.tags.map(t => <span key={t}>{t}</span>)}</div>
                <Link className="course-link" data-testid={`${c.id}-details-button`} to={c.path}>View programme page <ArrowUpRight size={16} /></Link>
              </div>
            </article>
          ))}
        </div>

        <div className="cert-head">
          <div><div className="eyebrow gold">Short-format certificates</div><h2>Focused skills. <em>Fast.</em></h2></div>
          <p>Two-to-five-week, practice-first certificate programmes that build one job-ready skill at a time.</p>
        </div>
        <div className="cert-grid">
          {certificates.map(c => (
            <Link to={c.path} className="cert-card" key={c.id} data-testid={c.id}>
              <div className="cert-card-img"><img src={c.image} alt={c.title} loading="lazy" /><span className="course-number">{c.num}</span></div>
              <div className="cert-card-body">
                <span className="eyebrow">Certificate</span>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
                <div className="tag-row">{c.tags.map(t => <span key={t}>{t}</span>)}</div>
                <span className="card-link">View programme <ArrowUpRight size={15} /></span>
              </div>
            </Link>
          ))}
        </div>

        <div className="compare-wrap">
          <div className="cert-head">
            <div><div className="eyebrow gold">Side by side</div><h2>Which programme <em>fits you?</em></h2></div>
            <p>Compare all seven ADVOKIT programmes at a glance — then open any of them for the full curriculum.</p>
          </div>
          <div className="compare-scroll">
            <table className="compare-table" data-testid="compare-table">
              <thead>
                <tr><th>Programme</th><th>Best for</th><th>Duration</th><th>Hours</th><th>Batch</th><th>Credential</th><th></th></tr>
              </thead>
              <tbody>
                {Object.values(programmes).map((p, i) => {
                  const snap = label => p.snapshot.find(([l]) => l === label)?.[1] || "—";
                  const bestFor = { "diploma": "Litigation & courtroom practice", "corporate": "Corporate & law-firm careers", "ai-research": "Faster AI-powered research", "arbitration": "Dispute resolution & ADR", "contract-drafting": "Transactional drafting", "data-privacy": "DPDP compliance roles", "compliance": "In-house compliance roles" }[p.key];
                  return (
                    <tr key={p.key} data-testid={`compare-row-${i + 1}`}>
                      <td><div className="compare-name"><span className="compare-num">{String(i + 1).padStart(2, "0")}</span><div><strong>{p.title.replace("Professional Certificate in ", "").replace("Certificate in ", "").replace("Diploma in ", "")}</strong><small>{p.type}</small></div></div></td>
                      <td>{bestFor}</td>
                      <td>{snap("Duration")}</td>
                      <td>{snap("Learning Hours")}</td>
                      <td>{snap("Batch Size")}</td>
                      <td>{p.key === "diploma" ? "Diploma" : "Certificate"}</td>
                      <td><Link className="card-link" to={p.path} data-testid={`compare-view-${i + 1}`}>Details <ArrowUpRight size={14} /></Link></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="compare-nudge" data-testid="compare-nudge">
            <p>Not sure which programme fits your goals?</p>
            <a className="whatsapp-btn" data-testid="compare-nudge-whatsapp-button" href={`${whatsapp}%20I%20need%20help%20choosing%20the%20right%20programme.`} target="_blank" rel="noreferrer">Ask us on WhatsApp <MessageCircle size={16} /></a>
          </div>
        </div>
      </section>

      <section className="path-section section-pad">
        <div className="path-copy">
          <div className="eyebrow gold">A career operating system</div>
          <h2>Build your career one <em>real skill</em> at a time.</h2>
          <p>ADVOKIT helps you move with intention, from your first classroom exercise to the day a client trusts your judgment.</p>
          <a className="light-btn" data-testid="path-whatsapp-button" href={whatsapp} target="_blank" rel="noreferrer">Start your journey <ArrowUpRight size={17} /></a>
        </div>
        <div className="path-steps">
          {["Learn", "Practice", "Build portfolio", "Get exposure", "Find opportunities", "Build your career"].map((step, i) => (
            <div className="path-step" key={step} data-testid={`path-step-${i + 1}`}><span>0{i + 1}</span><strong>{step}</strong>{i < 5 && <ChevronRight size={17} />}</div>
          ))}
        </div>
      </section>

      <section id="students-say" className="students-say">
        <div className="students-inner">
          <div className="students-intro">
            <div className="eyebrow gold">Students say</div>
            <h2>Real students. <em>Real courtroom confidence.</em></h2>
            <p>From classrooms across India to chambers and courtrooms — hear what learning by doing feels like.</p>
            <div className="students-stat" data-testid="students-trust-stat"><strong>2,500+</strong><span>students trust and study at ADVOKIT</span></div>
            <div className="seats-badge" data-testid="priority-seats-badge"><Sparkles size={14} /> Only 26 priority seats left this month <a data-testid="priority-seats-whatsapp-link" href={whatsapp} target="_blank" rel="noreferrer">Claim yours</a></div>
          </div>
          <div className="students-grid">
            {testimonials.map((t, i) => (
              <article className="student-card" key={t.name} data-testid={`testimonial-${i + 1}`}>
                <div className="stars">★★★★★</div>
                <p>“{t.quote}”</p>
                <div className="person">{t.photo ? <img src={t.photo} alt={t.name} /> : <span>{t.name.split(" ").map(n => n[0]).join("")}</span>}<div><strong>{t.name}</strong><small>{t.role} · {t.college}</small></div></div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="career-club" className="club-section" data-testid="career-club-section">
        <div className="club-inner">
          <div className="club-copy">
            <div className="eyebrow"><span />Lexintern Career Club</div>
            <h2>Everything a law student needs to get <em>career-ready.</em></h2>
            <p>ADVOKIT's sister community for law students — academic support, drafting resources, moot court training, mentorship and member-only career alerts, all in one club.</p>
            <div className="club-price-row" data-testid="club-price"><span className="club-price">₹99<small> / month</small></span><span className="club-note">Cancel anytime</span></div>
            <div className="club-perks" data-testid="club-perks">90+ resources & tools · Weekly new content · Workshops & career alerts</div>
            <a className="primary-btn" data-testid="join-career-club-button" href="https://lexintern.co.in/" target="_blank" rel="noreferrer">Join the Career Club <ArrowUpRight size={17} /></a>
          </div>
          <div className="club-pillars">
            {[
              [GraduationCap, "Academic Support", "Semester notes, study planners, flashcards & case-law summaries"],
              [Scale, "Practical Legal Skills", "Walk into a courtroom like you belong there"],
              [Gavel, "Moot Court & Advocacy", "From memorial to oral rounds — argue to win"],
              [Compass, "Career & Mentorship", "A guided path from classroom to chambers"],
              [FileText, "Drafting & Research", "A complete drafting chamber, open round the clock"],
              [Users, "Community & Events", "A circle of ambitious law students, always in session"],
              [Bell, "Member-Only Career Alerts", "Fresh resources and opportunities every month"],
            ].map(([Icon, title, desc], i) => (
              <div className="club-pillar" key={title} data-testid={`club-pillar-${i + 1}`}><Icon size={18} /><div><strong>{title}</strong><span>{desc}</span></div></div>
            ))}
          </div>
        </div>
      </section>

      <section id="google-reviews" className="greviews-section">
        <div className="greviews-inner">
          <div className="greviews-summary">
            <img src="/images/google-g.svg" alt="Google" className="g-logo" />
            <div className="eyebrow"><span />Google reviews</div>
            <h2>Loved by learners, <em>rated on Google.</em></h2>
            <div className="greviews-score" data-testid="google-rating-score">
              <strong>{googleReviews.rating}</strong>
              <div><div className="stars gstars">★★★★★</div><span>Based on {googleReviews.count} Google reviews</span></div>
            </div>
            <a className="greviews-link" data-testid="google-reviews-link" href={googleReviews.url} target="_blank" rel="noreferrer">Read all reviews on Google <ArrowUpRight size={15} /></a>
          </div>
          <div className="greviews-grid">
            {googleReviews.reviews.map((r, i) => (
              <article className="greview-card" key={r.name} data-testid={`google-review-${i + 1}`}>
                <div className="greview-head">
                  <span className={`g-avatar g${i % 4}`}>{r.name[0]}</span>
                  <div><strong>{r.name}</strong><small>Posted on Google</small></div>
                  <img src="/images/google-g.svg" alt="" className="g-mini" />
                </div>
                <div className="stars gstars">★★★★★</div>
                <p>“{r.text}”</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="insights" className="section-pad insights-section">
        <div className="section-heading-row">
          <div><div className="eyebrow">The ADVOKIT journal</div><h2>Ideas for the <em>working lawyer.</em></h2></div>
          <p data-testid="blog-archive-count">{articles.length} articles in the ADVOKIT archive</p>
        </div>
        <div className="journal-grid home-journal-grid">
          {featuredArticles.map((article, i) => <ArticleCard key={article.slug} article={article} index={i + 1} />)}
        </div>
        <div className="journal-cta-row">
          <Link to="/blogs" className="primary-btn" data-testid="browse-journal-button">Browse all articles <ArrowUpRight size={17} /></Link>
        </div>
        <div className="resource-inline" id="resources" data-testid="resource-card-1">
          <FileText size={25} /><span>Looking for practical tools?</span>
          <button data-testid="resource-brochures-button" onClick={() => go("courses")}>View programme brochures <ArrowUpRight size={15} /></button>
        </div>
      </section>

      <section id="contact" className="contact-section section-pad">
        <div className="contact-copy">
          <div className="eyebrow gold">Your next move starts here</div>
          <h2>Ready to make practice your <em>advantage?</em></h2>
          <p>Read the programme brochure, then message ADVOKIT directly for current batches, fees and enrolment guidance.</p>
          <div className="contact-details">
            <a data-testid="contact-phone-link" href="tel:+917309606226">+91 7309 606 226</a>
            <a data-testid="contact-email-link" href="mailto:info@advokit.co.in">info@advokit.co.in</a>
          </div>
        </div>
        <div className="contact-action-card">
          <MessageCircle size={34} />
          <h3>Talk to the ADVOKIT team</h3>
          <p>Course details and enrolment are handled personally. Send us a WhatsApp message and we’ll guide you from there.</p>
          <a className="whatsapp-btn" data-testid="contact-whatsapp-button" href={whatsapp} target="_blank" rel="noreferrer">Chat on WhatsApp <ArrowUpRight size={17} /></a>
        </div>
      </section>

      <section id="location" className="map-section">
        <div className="map-inner">
          <div className="map-card">
            <div className="eyebrow gold"><span />Visit us</div>
            <h2>Find us in <em>Hazratganj, Lucknow.</em></h2>
            <p className="map-address" data-testid="office-address">{address}</p>
            <div className="map-contact">
              <a data-testid="map-phone-link" href="tel:+917309606226">+91 7309 606 226</a>
              <a data-testid="map-email-link" href="mailto:info@advokit.co.in">info@advokit.co.in</a>
            </div>
            <div><a className="light-btn" data-testid="get-directions-button" href={directionsUrl} target="_blank" rel="noreferrer">Get directions <ArrowUpRight size={16} /></a></div>
          </div>
          <div className="map-frame" data-testid="google-map-embed">
            <iframe title="ADVOKIT office location — Dube Chambers, Hazratganj, Lucknow" src={mapEmbed} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>
      </section>
    </main>
  );
}
